import {
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  XCircleIcon,
} from '@heroicons/react/20/solid'
import cx from 'classnames'
import React, { ComponentProps, useContext, useEffect, useState } from 'react'
import { Portal } from 'react-portal'

import { Button } from '../button'
import { X } from '../icons'
import { Heading } from '../typography'
import AlertContext from './context'

export const DEFAULT_ALERT_DISMISS_TIMEOUT = 5000

export type AlertProps = {
  /** Alert message variant. */
  variant: 'danger' | 'warning' | 'info' | 'success'
  /** Alert message body. */
  children: React.ReactNode
  /** Indicates whether alert should be dismissed automatically. */
  autoDismiss?: boolean
  /** Indicates whether alert icon should be centered or at flex start.
   *  Default is true (centered). */
  iconCenter?: boolean
  /** Indicates whether alert close button should be centered or at flex start.
   *  Default is true (centered). */
  xIconCenter?: boolean
  /** Close alert callback. */
  onClose?: () => void
}

/** Returns the icon of the alert variant. */
const getIcon = (variant: string) => {
  const className = cx('h-6 w-6', {
    'text-fill-success': variant === 'success',
    'text-fill-critical': variant === 'danger',
    'text-fill-warning': variant === 'warning',
    'text-fill-info': variant === 'info',
  })

  switch (variant) {
    case 'success':
      return <CheckCircleIcon className={className} aria-hidden="true" />
    case 'danger':
      return <XCircleIcon className={className} aria-hidden="true" />
    case 'warning':
      return <ExclamationCircleIcon className={className} aria-hidden="true" />
    case 'info':
      return <InformationCircleIcon className={className} aria-hidden="true" />
  }
}

// TODO: Add exit transition.
/** Alert message that appers on the top of the page. */
export default function Alert({
  variant,
  children,
  autoDismiss,
  onClose,
  iconCenter = true,
  xIconCenter = true,
  ...rest
}: AlertProps) {
  const { parentNodeId } = useContext(AlertContext)
  const [isOpen, setIsOpen] = useState(true)

  useEffect(() => {
    if (autoDismiss && isOpen) {
      const t = setTimeout(() => {
        setIsOpen(false)
      }, DEFAULT_ALERT_DISMISS_TIMEOUT)

      return () => {
        clearTimeout(t)
      }
    }
  }, [autoDismiss, isOpen])

  if (!isOpen || !parentNodeId) {
    return null
  }

  const handleClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  // TODO(color-tokens): update styles of component
  return (
    <Portal node={document.getElementById(parentNodeId)}>
      <div
        data-testid="alert"
        className={cx('mx-auto rounded-md p-4 sm:w-[90%]', {
          'bg-green-100 dark:bg-green-200': variant === 'success',
          'bg-red-100 dark:bg-red-200': variant === 'danger',
          'bg-amber-100 dark:bg-amber-200': variant === 'warning',
          'bg-blue-100 dark:bg-blue-200': variant === 'info',
        })}
        {...rest}
      >
        <div className={cx('flex', { 'items-center': iconCenter })}>
          <div className="flex-shrink-0">{getIcon(variant)}</div>
          <div
            className={cx('ml-3 font-medium', {
              'text-green-500': variant === 'success',
              'text-red-500': variant === 'danger',
              'text-amber-500': variant === 'warning',
              'text-blue-500': variant === 'info',
            })}
          >
            {children}
          </div>
          <div
            className={cx('flex flex-auto flex-row-reverse', {
              'self-start': !xIconCenter,
            })}
          >
            <Button
              data-testid="closeButton"
              aria-label="Close"
              onClick={handleClose}
              size="icon"
              className="border-0 border-none shadow-none"
            >
              <X
                className={cx('h-5 w-5', {
                  'text-green-500': variant === 'success',
                  'text-red-500': variant === 'danger',
                  'text-amber-500': variant === 'warning',
                  'text-blue-500': variant === 'info',
                })}
              />
            </Button>
          </div>
        </div>
      </div>
    </Portal>
  )
}

type AlertTitleProps = ComponentProps<'h3'> & Pick<AlertProps, 'variant'>
export const AlertTitle = ({
  variant,
  className,
  ...rest
}: AlertTitleProps) => {
  return (
    <Heading
      level={3}
      className={cx(
        'font-medium',
        {
          'text-green-800': variant === 'success',
          'text-red-800': variant === 'danger',
          'text-amber-800': variant === 'warning',
          'text-blue-800': variant === 'info',
        },
        className,
      )}
      {...rest}
    />
  )
}

type AlertBodyProps = ComponentProps<'div'> & Pick<AlertProps, 'variant'>
export const AlertBody = ({ variant, className, ...rest }: AlertBodyProps) => {
  return (
    <div
      className={cx(
        'mt-2 text-sm font-normal',
        {
          'text-green-700': variant === 'success',
          'text-red-700': variant === 'danger',
          'text-amber-700': variant === 'warning',
          'text-blue-700': variant === 'info',
        },
        className,
      )}
      {...rest}
    />
  )
}

Alert.Title = AlertTitle
Alert.Body = AlertBody
Alert.defaultProps = {
  autoDismiss: true,
}
